import { gql } from '@apollo/client';
import { FilterFragment } from '../filters';
import {
  CreatorFields,
  DatePickerFieldsFragment,
  DateRangeFieldsFragment,
  EntityPermissionFieldsFragment,
  SchemaFieldsFragment,
} from '../fragments';
import { RichTextFieldsFragment } from '../richText/fragments';
import { ExternalEmbedFieldsFragment } from '../externalEmbed/fragments';
import {
  ImageEmbedFieldsFragment,
  PostUploadConfigFieldsFragment,
} from '../imageEmbed/fragments';
import { AnalysisSummaryFieldsFragment } from '../analysis/fragments';
import { LlmSubjectSummaryFieldsFragment } from '../llm/fragments';

export const SubFolderFields = gql`
  fragment SubFolderFields on SubFolder {
    id
    name
    parentId
    isEmpty
    isDisabled
  }
`;

export const DashboardTemplateFieldsFragment = gql`
  fragment DashboardTemplateFields on Dashboard {
    baseId
    name
    description
    isPublished
    isFavorite
    isSharedWithMe
    path {
      ...SubFolderFields
    }
    permission {
      ...EntityPermissionFields
    }
    isLatestVersion
  }
  ${SubFolderFields}
  ${EntityPermissionFieldsFragment}
`;

export const DashboardFolderFieldsFragment = gql`
  fragment DashboardFolderFields on DashboardFolder {
    id
    name
    parentId
    isDisabled
    path {
      ...SubFolderFields
    }
    subFolders {
      ...SubFolderFields
    }
    templates {
      ...DashboardTemplateFields
    }
  }
  ${SubFolderFields}
  ${DashboardTemplateFieldsFragment}
`;

export const DashboardFilterFieldsFragment = gql`
  fragment DashboardFilterFields on DashboardFilter {
    dateRange {
      ...DateRangeFields
    }
    filter {
      ...FilterFields
    }
    schema {
      ...SchemaFields
    }
    datePicker {
      ...DatePickerFields
    }
  }
  ${FilterFragment}
  ${DateRangeFieldsFragment}
  ${SchemaFieldsFragment}
  ${DatePickerFieldsFragment}
`;

export const DashboardAnalysisColumnFieldsFragment = gql`
  fragment DashboardAnalysisColumnFields on DashboardAnalysisColumn {
    colSpan
    analysis {
      ...AnalysisSummaryInfoFields
    }
  }
  ${AnalysisSummaryFieldsFragment}
`;

export const DashboardRichTextColumnFieldsFragment = gql`
  fragment DashboardRichTextColumnFields on DashboardRichTextColumn {
    colSpan
    richText {
      ...RichTextFields
    }
  }
  ${RichTextFieldsFragment}
`;

export const DashboardExternalEmbedColumnFieldsFragment = gql`
  fragment DashboardExternalEmbedColumnFields on DashboardExternalEmbedColumn {
    colSpan
    externalEmbed {
      ...ExternalEmbedFields
    }
  }
  ${ExternalEmbedFieldsFragment}
`;

export const DashboardImageEmbedColumnFieldsFragment = gql`
  fragment DashboardImageEmbedColumnFields on DashboardImageEmbedColumn {
    colSpan
    imageEmbed {
      ...ImageEmbedFields
    }
  }
  ${ImageEmbedFieldsFragment}
`;

export const DashboardColumnFieldsFragment = gql`
  fragment DashboardColumnFields on DashboardColumn {
    ... on DashboardAnalysisColumn {
      ...DashboardAnalysisColumnFields
    }
    ... on DashboardRichTextColumn {
      ...DashboardRichTextColumnFields
    }
    ... on DashboardExternalEmbedColumn {
      ...DashboardExternalEmbedColumnFields
    }
    ... on DashboardImageEmbedColumn {
      ...DashboardImageEmbedColumnFields
    }
  }
  ${DashboardAnalysisColumnFieldsFragment}
  ${DashboardRichTextColumnFieldsFragment}
  ${DashboardExternalEmbedColumnFieldsFragment}
  ${DashboardImageEmbedColumnFieldsFragment}
`;

export const DashboardRowFieldsFragment = gql`
  fragment DashboardRowFields on DashboardRow {
    id
    columns {
      ...DashboardColumnFields
    }
    minHeight
  }
  ${DashboardColumnFieldsFragment}
`;

export const DashboardInstanceFieldsFragment = gql`
  fragment DashboardInstanceFields on Dashboard {
    ...DashboardTemplateFields
    id
    exportUrl
    createdAt
    versionCreatedAt
    versionEditedAt
    refreshedAt
    isDefault
    isDeleted
    isLatestInstance
    rows {
      ...DashboardRowFields
    }
    versionCreator {
      ...CreatorFields
    }
    versionEditor {
      ...CreatorFields
    }
    filter {
      ...DashboardFilterFields
    }
    llmSummary {
      ...LlmSubjectSummaryFields
    }
  }
  ${DashboardFilterFieldsFragment}
  ${DashboardRowFieldsFragment}
  ${CreatorFields}
  ${LlmSubjectSummaryFieldsFragment}
  ${DashboardTemplateFieldsFragment}
`;

export const DashboardImageEmbedUploadFieldsFragment = gql`
  fragment DashboardImageEmbedUploadConfigFields on DashboardImageEmbedUploadConfig {
    imageEmbed {
      ...ImageEmbedFields
    }
    postUploadConfig {
      ...PostUploadConfigFields
    }
  }
  ${ImageEmbedFieldsFragment}
  ${PostUploadConfigFieldsFragment}
`;
